import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ConversationPreview from './ConversationPreview';
import { 
  getUserDisplayName, 
  getUserInitials, 
  getVisibleConversations, 
  getRemainingCount,
  type UserConversationGroup as UserGroup 
} from '@/utils/conversationGroupingUtils';
import type { Conversation } from '@/types/chat';

interface UserConversationGroupProps {
  group: UserGroup;
  onSelectConversation: (conversation: Conversation) => void;
  visibleCount: number;
  onLoadMore: (userId: string) => void;
  onCollapse: (userId: string) => void;
  highlightedConversationId?: string;
}

const UserConversationGroup: React.FC<UserConversationGroupProps> = ({
  group,
  onSelectConversation,
  visibleCount,
  onLoadMore,
  onCollapse,
  highlightedConversationId
}) => {
  const displayName = getUserDisplayName(group.user);
  const initials = getUserInitials(group.user);
  const visibleConversations = getVisibleConversations(group, visibleCount);
  const remainingCount = getRemainingCount(group, visibleCount);
  const canLoadMore = remainingCount > 0;
  const isExpanded = visibleCount > 3;

  const handleLoadMore = () => {
    onLoadMore(group.userId);
  };

  const handleCollapse = () => {
    onCollapse(group.userId);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* User Header */}
        <div className="p-3 sm:p-4 border-b border-border/50 bg-muted/20">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 sm:h-12 sm:w-12 border-2 border-white shadow-sm">
              <AvatarImage src={group.user.avatar_url || undefined} alt={displayName} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0">
                <h3 className="font-medium text-sm sm:text-base truncate m-0 leading-tight">{displayName}</h3>
                {group.unreadCount > 0 && (
                  <Badge variant="default" className="h-5 sm:h-6 text-xs">
                    {group.unreadCount}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground mt-1">
                <span>{group.totalCount} conversation{group.totalCount !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Conversation Previews */}
        <div className="divide-y divide-border/30">
          {visibleConversations.map((conversation, index) => (
            <ConversationPreview
              key={conversation.id}
              conversation={conversation}
              onSelect={onSelectConversation}
              isHighlighted={conversation.id === highlightedConversationId}
              isCompact={index > 0}
            />
          ))}
        </div>

        {/* Load More / Collapse Actions */}
        {(canLoadMore || isExpanded) && (
          <div className="p-3 border-t border-border/50 bg-muted/10">
            <div className="flex items-center justify-center gap-4 text-xs">
              {canLoadMore && (
                <button
                  onClick={handleLoadMore}
                  className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <ChevronDown className="h-4 w-4" />
                  <span>Load 3 more</span>
                </button>
              )}
              
              {isExpanded && (
                <button
                  onClick={handleCollapse}
                  className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <ChevronUp className="h-4 w-4" />
                  <span>Show less</span>
                </button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserConversationGroup;